import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  CheckCircle2, Circle, ClipboardList, Rocket, Users, Settings,
  X, RefreshCw, Trophy, ShoppingBag, UserPlus,
  Clock, Bell, Blocks, FileText, ArrowRight, Pencil
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_ICONS = {
  complete_profile: Settings,
  invite_member: UserPlus,
  add_products: ShoppingBag,
  import_clients: Users,
  setup_attendance: Clock,
  configure_notifications: Bell,
  review_modules: Blocks,
  first_transaction: FileText,
};

export default function OnboardingChecklistPage() {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchChecklist = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/onboarding-checklist`);
      setChecklist(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchChecklist(); }, [fetchChecklist]);

  const toggleStep = async (stepKey, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'not_started' : 'completed';
    try {
      await axios.put(`${API}/api/onboarding-checklist/step/${stepKey}`, { status: newStatus });
      fetchChecklist();
    } catch {}
  };

  const dismissChecklist = async () => {
    try {
      await axios.put(`${API}/api/onboarding-checklist/dismiss`, {});
      fetchChecklist();
    } catch {}
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  const steps = checklist?.steps || [];
  const completed = steps.filter(s => s.status === 'completed').length;
  const total = steps.length;
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="onboarding-checklist-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Company Setup<PageInfoTooltip pageKey="onboarding" /></h1>
          <p className="text-gray-medium text-sm mt-1">Your business has been pre-configured with sample data. Edit each section with your own company details.</p>
        </div>
        {!checklist?.dismissed && completed < total && (
          <Button variant="outline" size="sm" onClick={dismissChecklist} data-testid="dismiss-checklist-btn">
            <X className="h-4 w-4 mr-1" /> Dismiss
          </Button>
        )}
      </div>

      {/* Progress Banner */}
      <Card className={cn("border-2", progress === 100 ? "border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50" : "border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50")}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {progress === 100 ? <Trophy className="h-8 w-8 text-emerald-500" /> : <Rocket className="h-8 w-8 text-blue-500" />}
              <div>
                <p className="font-heading font-bold text-lg text-text-primary">
                  {progress === 100 ? 'All Steps Pre-Configured!' : `Step ${completed + 1} of ${total}`}
                </p>
                <p className="text-xs text-gray-medium">
                  {progress === 100
                    ? 'Sample data has been loaded. Edit each section with your real company information, then clear demo data when ready.'
                    : `${completed} completed, ${total - completed} remaining — edit each with your company data`}
                </p>
              </div>
            </div>
            <Badge className={cn("text-sm px-3 py-1", progress === 100 ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700")}>
              {progress}%
            </Badge>
          </div>
          <Progress value={progress} className="h-2.5" />
        </CardContent>
      </Card>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step, idx) => {
          const StepIcon = STEP_ICONS[step.key] || Circle;
          const isDone = step.status === 'completed';
          const isAutoFilled = step.auto_populated;
          const isNext = !isDone && steps.slice(0, idx).every(s => s.status === 'completed');
          return (
            <Card
              key={step.key}
              className={cn(
                "border transition-all",
                isDone ? "border-emerald-200 bg-emerald-50/30" : isNext ? "border-blue-300 bg-blue-50/30 shadow-sm" : "border-gray-border bg-white"
              )}
              data-testid={`checklist-step-${step.key}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => toggleStep(step.key, step.status)}
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all",
                      isDone ? "bg-emerald-500 border-emerald-500 text-white" : isNext ? "bg-blue-100 border-blue-400 text-blue-600" : "bg-gray-50 border-gray-200 text-gray-400"
                    )}
                  >
                    {isDone ? <CheckCircle2 className="h-5 w-5" /> : <span className="text-sm font-bold">{idx + 1}</span>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <StepIcon className={cn("h-4 w-4 flex-shrink-0", isDone ? "text-emerald-500" : isNext ? "text-blue-500" : "text-gray-400")} />
                      <p className={cn("font-medium text-sm", isDone && !isAutoFilled ? "text-emerald-700 line-through" : isDone ? "text-emerald-700" : "text-text-primary")}>{step.title}</p>
                      {isAutoFilled && isDone && (
                        <Badge className="bg-violet-100 text-violet-600 text-[9px] gap-0.5">Pre-filled with sample data</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-medium mt-0.5 ml-6">
                      {isAutoFilled && isDone
                        ? `Sample data loaded — edit with your real company details`
                        : step.description}
                    </p>
                  </div>
                  {step.nav_path && (
                    <Button
                      size="sm"
                      className={cn("flex-shrink-0 gap-1.5 text-xs",
                        isDone && isAutoFilled
                          ? "bg-violet-600 hover:bg-violet-700"
                          : isDone ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          : isNext ? "bg-blue-600 hover:bg-blue-700"
                          : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                      )}
                      onClick={() => navigate(step.nav_path)}
                      data-testid={`go-to-${step.key}`}
                    >
                      {isDone && isAutoFilled ? (
                        <><Pencil className="h-3 w-3" /> Edit with Your Data</>
                      ) : isDone ? (
                        <><Pencil className="h-3 w-3" /> Review</>
                      ) : (
                        <>Go to Setup <ArrowRight className="h-3 w-3" /></>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {progress === 100 && (
        <Card className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0">
          <CardContent className="p-6 text-center">
            <Trophy className="h-12 w-12 mx-auto mb-3 text-white/90" />
            <h3 className="font-heading font-bold text-xl">Your Business is Pre-Configured!</h3>
            <p className="text-sm text-white/80 mt-2">
              Sample data has been loaded across all sections. Edit each step with your real company information.
              Once done, use the "Clear Demo Data" banner to remove all sample records — your own data will be preserved.
            </p>
            <Button variant="outline" className="mt-4 bg-white/20 border-white/40 text-white hover:bg-white/30" onClick={() => navigate('/dashboard')}>
              Go to Dashboard <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
